import { Serializable } from "./serializable";
import Cellar from "./cellar";
import { UtilitiesFunctions } from "@/helpers";
import Address from "@/models/address";
import ExperienceAvailabilityForDay from '@/models/experience-availability';
import { formatISO } from 'date-fns';

export interface InternalEventDate {
  event_date: string;
  text?: string;
  adultsCount?: number;
  maxAdults: number;
  minAdults: number;
  created_at?: string;
  experience_id?: string;
  id?: string;
  max_slots?: number;
  min_slots?: number;
  override_active?: any;
  override_max_adults?: any;
  override_min_adults?: any;
  override_tags?: any;
  updated_at?: any;
  tags?: any[] ;
}

export interface PriceData {
  id: number;
  title?: string;
  price: string;
  priceCurrency?: string;
  priceSymbol?: string;
  quantity: number;
  giftQuantity: number;
  disable: boolean;
  titleTranslations: any
}

export default class Experience implements Serializable<Experience> {
  id?: string;
  acceptPaypalPayments: boolean;
  acceptStripePayments: boolean;
  paypalTargetAccountId: boolean;
  address?: Address;
  avatarImage?: any;
  title?: string;
  summary?: string;
  days?: any;
  description?: string;
  images?: any[];
  experiencePriceExtras: PriceData[]
  experiencePriceLabels: PriceData[]
  cover?: string;
  eventDate?: string;
  experienceLanguages?: any[] = [];
  experienceTypeTags?: any[] = [];
  activityTypeTags?: any[] = [];
  moodTypeTags?: any[] = [];
  experienceLanguagesTags?: any[] = [];
  experienceServicesTags?: any[] = [];
  duration?: string;
  cellar?: Cellar;
  price?: string;
  priceLabel?: string;
  priceSymbol?: string;
  priceCurrency?: string;
  priceType?: string;
  price2?: string;
  price2Label?: string;
  price2Symbol?: string;
  price2Currency?: string;
  cardLink?: string = "experience";
  cardType: string = "experienceCard";
  timetable?: string = "";
  metaTitle?: string;
  metaDescription?: string;
  closings?: {first: string, last: string}[];
  maxAdults?: number;
  minAdults?:number;
  stripeAccount?: string;
  stripeConnected?: boolean;
  paypalAccount?: string;
  eventDates?: {
    event_date: string;
    text?: string;
    adults_count?: number;
    max_adults: number;
    min_adults: number;
    created_at?: string;
    experience_id?: string;
    id?: string;
    max_slots?: number;
    min_slots?: number;
    override_active?: any;
    override_max_adults?: any;
    override_min_adults?: any;
    override_tags?: any;
    updated_at?: any;
    tags?: any[] ;
  }[];
  instantBooking: boolean;
  price2Visible?: boolean;
  enableGifts: boolean;
  minParties: number;
  maxParties: number;
  isInquiry: boolean;
  isGiftOnly: boolean;

  deserialize(input: any): Experience {
    if (input != null) {
      this.id = input.id;
      this.title = input.title || "";
      if (input.company && input.company.company_closings) {
        this.closings = input.company.company_closings.map(closing => ({
          first: closing.first,
          last: closing.last
        }))
      }
      if (input.company && input.company.avatar_image)
        this.avatarImage = input.company.avatar_image.urls
          ? input.company.avatar_image.urls.s
          : "";
      if (input.place)
        this.address = new Address().deserialize(input.place) || null;
      if (input.summary)
        this.summary = input.summary.replace(/\n/g, "<br>") || "";
      this.days = input.days;
      if (input.description)
        this.description = input.description.replace(/\n/g, "<br>") || "";
      if (input.photos && input.photos.length > 0) {
        for (let i = 0; i < input.photos.length; i++) {
          if (!this.images) this.images = [];
          if (input.photos[i].main) {
            // if main === true the image will be first in the carousel
            this.images.unshift(input.photos[i].urls.v); // Main carousel image
            if (!input.photos[i].cover) continue;
          }
          if (input.photos[i].cover) {
            this.cover = input.photos[i].urls.m || null; // Experience cover image
          }
          if (input.photos[i].urls) this.images.push(input.photos[i].urls.v); // Exp carousel image
        }
      }
      this.experiencePriceLabels = input.experience_price_labels.map(e=> ({
        'id': e.id,
        'title': e.title,
        'price': e.price,
        'priceCurrency': e.price_currency,
        'priceSymbol': e.price_symbol,
        'quantity': 0,
      })) || [];

      this.experiencePriceExtras = input.experience_price_extras.map(e=> ({
        'id': e.id,
        'title': e.title,
        'price': e.price,
        'priceCurrency': e.price_currency,
        'priceSymbol': e.price_symbol,
        'quantity': 0,
      })) || [];
      if (input.cover)
        this.cover = input.cover.m;
      if (input.company)
        this.cellar = new Cellar().deserialize(input.company) || null;
      this.duration = input.duration;
      if (input.event_date) this.eventDate = input.event_date;
      this.price = input.price || null;
      this.priceSymbol = input.price_symbol || null;
      this.priceCurrency = input.price_currency || null;
      this.priceLabel = input.price01_label || null;
      this.price2 = this.experiencePriceLabels.length ? this.experiencePriceLabels[0].price : null;
      this.price2Symbol = this.experiencePriceLabels.length > 0 ? this.experiencePriceLabels[0].priceSymbol : null;
      this.price2Label = this.experiencePriceLabels.length > 0 ? this.experiencePriceLabels[0].title : null;
      this.price2Currency = this.experiencePriceLabels.length > 0 ? this.experiencePriceLabels[0].priceCurrency : null;
      this.priceType = input.price_description || null;
      this.duration = input.duration || "";
      this.experienceLanguages = input.experience_languages || [];
      this.metaDescription = input.meta_description;
      this.metaTitle = input.meta_title;
      this.price2Visible = this.experiencePriceLabels.length > 0;

      if (input.tags) {
        this.experienceTypeTags =
          UtilitiesFunctions.buildTagsArrayV2(input.tags, "exptype") || [];
        this.activityTypeTags =
          UtilitiesFunctions.buildTagsArrayV2(input.tags, "activity") || [];
        this.moodTypeTags =
          UtilitiesFunctions.buildTagsArrayV2(input.tags, "mood") || [];
        this.experienceLanguagesTags =
          UtilitiesFunctions.buildTagsArrayV2(input.tags, "spklang") || [];
        this.experienceServicesTags =
          UtilitiesFunctions.buildTagsArrayV2(input.tags, "services") || [];
      }
      this.timetable = input.timetable;
      this.eventDates = input.event_dates;
      this.maxAdults = input.max_adults;
      this.minAdults = input.min_adults;
      this.acceptPaypalPayments = input.accept_paypal_payments;
      this.acceptStripePayments = input.accept_stripe_payments;
      if (input.company) {
        this.stripeConnected = input.company.stripe_connected;
        this.stripeAccount = input.company.stripe_account;
        this.paypalAccount = input.company.paypal_account;
      }
      this.instantBooking = input.instant_booking;
    }
    this.enableGifts = input.enable_gifts;
    this.minParties = input.min_parties_to_enable;
    this.maxParties = input.max_parties;
    this.isInquiry =  input.is_inquiry;
    this.isGiftOnly =  input.is_gift_only;
    return this;
  }

  isDayAvailable(availabilities: ExperienceAvailabilityForDay[], day: Date) {
    let hoursAvailable = this.availableTimesFor(availabilities, day);
    return hoursAvailable.length > 0;
  }

  availableTimesFor(availabilitiesForDay: ExperienceAvailabilityForDay[], day: Date) {
    const dayStr = formatISO(day, { representation: 'date' });
    const availabilityForDay: ExperienceAvailabilityForDay|undefined = availabilitiesForDay.find(a => a.day === dayStr);
    if (!availabilityForDay) return [];
    const timetable = availabilityForDay
      .availabilities
      .filter(av => av.active)
      .filter(av => av.adultsCount < av.calculcatedMaxAdults)
      .map(av => av.time);
    return timetable;
  }
}
